import { AppendedPatternOfCareProviderModel, PatternOfCareProvidersTableModel } from "../interfaces";
import { Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import PatternOfCareProvidersService from "../patternOfCareProvidersService";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { buildTableData, customSort } from "../patternOfCareSharedMethods";
import { Roles, UserContext } from "@app/user";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { toast } from "react-toastify";
import { Link, useLocation } from "react-router-dom";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { ExportType, HSDFormatType } from "@app/exports/enumerations";
import { RegionGranularity } from "@app/region-sets/regionSet";
import { checkForEnter } from "@app/shared/utility";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

const columnConfig: ColumnConfig = {
  address: {
    displayText: "Address",
    sizeContentToFit: true,
    testId: "address",
  },
  name: {
    displayText: "Name",
    sizeContentToFit: true,
    testId: "name",
  },
  specialty: {
    displayText: "Specialty",
    sizeContentToFit: true,
    testId: "specialty",
  }
}

interface AppendedProvidersProps {
  exportEndpoint: string;
  loadingAppendedProviders: boolean;
  setLoadingAppendedProviders: Dispatch<SetStateAction<boolean>>;
  providerDetails: AppendedPatternOfCareProviderModel[];
  setProviderDetails: Dispatch<SetStateAction<AppendedPatternOfCareProviderModel[]>>;
}

export const PatternOfCareAppendedProviders: FC<AppendedProvidersProps> = (props: AppendedProvidersProps) => {
  const projectContext = useContext(CountyProjectContext);
  const user = useContext(UserContext);
  const location = useLocation();
  const patternOfCareService = useRef(new PatternOfCareProvidersService()).current;
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [tableData, setTableData] = useState<PatternOfCareProvidersTableModel[]>([]);
  const [searchString, setSearchString] = useState("");
  const [skip, setSkip] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const take = 50;

  useEffect(() => {
    // prevent making multiple simultaneous requests
    if (props.loadingAppendedProviders) {
      return;
    }
    props.setLoadingAppendedProviders(true);
    patternOfCareService.getAppendedProviders(
      projectContext.project.projectId,
      projectContext.runConfiguration.projectRunConfigurationId,
      projectContext.adequacyResult.details[0].regionSetId,
      projectContext.regionFilters.specialty
    ).then((appendedProviders) => {
      const newTableData = buildTableData(appendedProviders, user);
      props.setProviderDetails(appendedProviders);
      setTableData(newTableData);
      props.setLoadingAppendedProviders(false);
    }, () => {
      // we don't need to notify the user of an error, because "no providers" returns a 400
      props.setProviderDetails([]);
      setTableData([]);
      props.setLoadingAppendedProviders(false);
    });
  }, []);

  const handleSearch = () => {
    const lowerCaseSearchString = searchString.toLowerCase();
    const filteredProviders = props.providerDetails.filter((pd) => {
      return (
        pd.displayName.toLowerCase().includes(lowerCaseSearchString)
        || `${pd.address}${!!pd.address2 ? ` ${pd.address2}` : ""} ${pd.city}, ${pd.state} ${pd.zip}`.toLowerCase().includes(lowerCaseSearchString)
        || pd.npi?.includes(lowerCaseSearchString)
      );
    });
    const newTableData = buildTableData(filteredProviders, user);
    setTableData(newTableData);
    setSkip(0);
    setIsFiltered(!!lowerCaseSearchString);
  };

  const handleClear = () => {
    const newTableData = buildTableData(props.providerDetails, user);
    setSearchString("");
    setTableData(newTableData);
    setSkip(0);
    setIsFiltered(false);
  };

  const handleRemoveClick = () => {
    props.setLoadingAppendedProviders(true);
    patternOfCareService.removeAppendedProviders(
      props.providerDetails.filter((pd) => selectedProviders.includes(pd.providerId.toString())).map((pd) => pd.appendedPatternOfCareProviderId),
      projectContext.project.projectId
    ).then(() => {
      const newProviderData = props.providerDetails.filter((pd) => !selectedProviders.includes(pd.providerId.toString()));
      const newTableData = buildTableData(newProviderData, user);
      props.setProviderDetails(newProviderData);
      setTableData(newTableData);
      props.setLoadingAppendedProviders(false);
      setSkip(0);
      setSelectedProviders([]);
      toast("Successfully removed appended providers", {
        containerId: "z-index-1030",
        type: "success"
      });
    }, () => {
      toast("An error occurred removing the appended providers. Please try again.", {
        containerId: "z-index-1030",
        type: "error"
      });
      props.setLoadingAppendedProviders(false);
    });
  }

  const selectedMemberships: number[] = [projectContext.runConfiguration.memberSetId];
  if (projectContext.isDualMembershipMode) {
    selectedMemberships.push(projectContext.compareToRunConfiguration.memberSetId);
  }

  const bulkActionBar = (
    <>
      <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
        to={{
          pathname: props.exportEndpoint,
          state: {
            activeModule: projectContext.module,
            activeNetworkType: ProviderNetworkType.Network,
            adequacyFilters: projectContext.adequacyFilters,
            availableExportTypes: [ExportType.ServicingProviders],
            defaultAppendPatternOfCareProviders: true,
            defaultHsdFormatType: HSDFormatType.MA,
            disableRegionFilters: true,
            disableSpecialtyGroupFilters: true,
            filters: projectContext.filters,
            granularity: RegionGranularity.County,
            initialNetworkAssessmentAnalysisType: projectContext.networkAssessmentAnalysisType,
            networkTypes: [ProviderNetworkType.Network],
            perspective: projectContext.perspective,
            projectResourceId: projectContext.project.resource.resourceId,
            projectResourceName: projectContext.project.resource.name,
            projectURL: location.pathname + location.search,
            regionSetIds: [projectContext.adequacyResult.details[0].regionSetId],
            showAppendNextClosestProviders: projectContext.runConfiguration.showNextClosestNetworkProviders,
            showAppendPatternOfCareProviders: true,
            specialtyGroupCodes: [projectContext.regionFilters.specialty],

            // new reportcustomization
            /* eslint-disable sort-keys */
            defaultReportType: ReportType.AdequacyServicingProvider,
            projectName: projectContext.project.resource.name,
            networkAssessmentAnalysisType: projectContext.networkAssessmentAnalysisType,
            primaryMembership: { alias: projectContext.project.memberSetAlias || projectContext.project.memberSetName, id: projectContext.project.memberSetId },
            secondaryMembership: { alias: projectContext.project.memberSetAlias2 || projectContext.project.memberSetName2, id: projectContext.project.memberSetId2 },
            selectedMemberships,
            selectedTimeslice: projectContext.runConfiguration.projectRunConfigurationId, // runConfigurationId
            isCompareMode: false,
            isViewingOpportunity: projectContext.adequacyFilters.opportunity,
            showPatternOfCare: projectContext.project.showPatternOfCareProviders,
            hasProspectProviders: !!projectContext.runConfiguration.prospectProviderSetId,
            hasMarketProviders: !!projectContext.runConfiguration.marketProviderSetId,
            hasNextClosestProviders: !!projectContext.runConfiguration.showNextClosestNetworkProviders,
            showAdvancedComparison: projectContext.project.showAdvancedComparison,
            appendPatternOfCareProviders: true,
            showExpandableSpecialties: !!projectContext.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
            /* eslint-enable */
          },
        }}
        data-testid="pattern-of-care__create-report-button"
        className={props.providerDetails.length === 0 ? "disabled" : undefined}
        onClick={props.providerDetails.length === 0 ? ((e) => e.preventDefault()) : undefined}
      >
        <QesIcon iconName={QesIcons.FontAwesome.CloudDownload} isDecorative={true} /> Create Report
      </Link>
      <QESButton
        qesType="secondary"
        qesTestId="pattern-of-care-panel__remove-appended-providers"
        disabled={selectedProviders.length === 0}
        onClick={handleRemoveClick}
      >
        Remove Appended Providers
      </QESButton>
    </>
  );

  const handleCheckboxChange = (selectedKeys: string[]) => {
    setSelectedProviders(selectedKeys);
  }

  const changePage = (newPage: number) => {
    const newSkip = (newPage - 1) * take;
    setSkip(newSkip);
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: changePage,
    recordsPerPage: take,
    skip: skip,
    totalRecords: tableData.length || 0,
  }

  const renderedTableData = useMemo(() => {
    const r = tableData.slice(skip, skip + take);
    return r;
  }, [skip, take, tableData]);

  const isSearchDisabled = searchString === "";

  return (
    <>
      <div className="pattern-of-care__input-fields row">
        <QESTextInput
          containerClassName="col-10"
          label="Filter Providers"
          value={searchString}
          disabled={props.providerDetails.length === 0}
          testId="pattern-of-care-appended-providers__search-input"
          onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, handleSearch)}
          onChange={(e) => setSearchString(e.target.value)}
          descriptiveText={`Search for a Provider Name, Address, ${user.roles.includes(Roles.TIN_VIEWER) ? "NPI, or TIN" : "or NPI"}`}
        />
        <QESButton
          qesTestId="pattern-of-care-appended-providers__search-button"
          onClick={handleSearch}
          qesType="secondary"
          className="col align-self-center"
          disabled={isSearchDisabled}
        >
          Search
        </QESButton>
        <QESButton
          qesTestId="pattern-of-care-appended-providers__clear-button"
          onClick={handleClear}
          qesType="secondary-danger"
          className="col align-self-center"
          disabled={!isFiltered}
        >
          Clear
        </QESButton>
      </div>

      <Waffles<PatternOfCareProvidersTableModel>
        bulkActionBar={bulkActionBar}
        columns={columnConfig}
        data={renderedTableData}
        enablePagination={true}
        paginationProps={paginationProps}
        loading={props.loadingAppendedProviders}
        showCheckboxes={true}
        handleCheckboxChange={handleCheckboxChange}
        selectedKeys={selectedProviders}
        testId="pattern-of-care-panel__provider-lookup"
        emptyTableMessage={props.providerDetails.length === 0 ? "No Pattern of Care Providers Appended for this county specialty combination" : undefined}
        customSort={customSort}
        fullWidth={true}
      />
    </>
  )
}